import Message from "@/components/common/Message";
import Modal from "@/components/common/Modal";
import LinkChip from "@/components/applications/history/LinkChip";
import { buttonClass } from "@/components/common/controls";
import { DownloadIcon } from "@/components/icons";
import { describeError } from "@/lib/api/client";
import { actionProps } from "@/lib/controlAnalytics";
import { saveFile } from "@/lib/hooks/resume";
import { useTailoredPdf } from "@/lib/hooks/tailorHistory";
import type { TailorRow } from "@/lib/tailorHistory";
import PdfPages from "./PdfPages";

/** How wide the pages are drawn: the dialog's inner width, so they are sharp in it. */
const PAGE_WIDTH = 704;

/** A file name from a job title: "Senior SRE" is "resume-senior-sre". */
function slug(text: string): string {
    const words = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    return words ? `resume-${words}` : "tailored-resume";
}

/** What the dialog needs of a resume; the history row has all of it, a button only the id. */
export type ViewedResume = Pick<TailorRow, "resumeId"> &
    Partial<Pick<TailorRow, "title" | "company" | "applyUrl" | "day" | "time" | "fills">>;

/**
 * One tailored resume, shown whole over the page: the PDF the extension
 * attaches when the person opens the posting's form.
 */
export default function TailoredResumeDialog({
    open,
    row,
    onClose,
}: {
    open: boolean;
    /** Kept after it closes, so the dialog fades out as it was. */
    row: ViewedResume | null;
    onClose: () => void;
}) {
    const pdf = useTailoredPdf(row?.resumeId ?? null);
    return (
        <Modal
            open={open && row !== null}
            onClose={onClose}
            analyticsContext={{ source: "Envoy.TailoredResume" }}
            width={760}
            title={row?.title ? `Resume for ${row.title}` : "Tailored resume"}
            description={
                row?.day
                    ? `${row.company ? `${row.company}, tailored` : "Tailored"} ${row.day} at ${row.time}.${
                          row.fills === false
                              ? " A newer resume was written for this job since; that one fills the form."
                              : " Open the posting and the extension fills the form with this resume."
                      }`
                    : undefined
            }
            footer={
                row ? (
                    <>
                        {row.applyUrl && <LinkChip href={row.applyUrl}>Apply</LinkChip>}
                        <button
                            {...actionProps("tailored_pdf_download_clicked", {
                                source: "Envoy.TailoredResume",
                                resume_id: row.resumeId,
                            })}
                            type="button"
                            disabled={!pdf.data}
                            onClick={() =>
                                pdf.data &&
                                saveFile(pdf.data, `${slug(row.title ?? row.company ?? "")}.pdf`)
                            }
                            className={buttonClass("primary", "md", "gap-1.5")}
                        >
                            <DownloadIcon className="size-3.5" strokeWidth={2.2} />
                            Download PDF
                        </button>
                    </>
                ) : undefined
            }
        >
            {pdf.isError ? (
                <Message>{describeError(pdf.error)}</Message>
            ) : (
                <div className="max-h-[70dvh] overflow-y-auto rounded-[10px] border border-line bg-card-raised p-3 max-[760px]:max-h-none">
                    <PdfPages
                        pdf={pdf.data}
                        width={PAGE_WIDTH}
                        label={row?.title ? `The resume for ${row.title}` : "The tailored resume"}
                        className="flex flex-col gap-3 [&>canvas]:rounded-[4px] [&>canvas]:shadow-card"
                        placeholder={
                            <p className="m-0 px-1 py-6 text-center text-[13px] text-fg-muted">
                                Reading the resume…
                            </p>
                        }
                    />
                </div>
            )}
        </Modal>
    );
}
